import {
  type Editor,
  findParentNode,
  findParentNodeClosestToPos,
} from "@tiptap/core"
import { type Schema } from "@tiptap/pm/model"
import type { EditorState, Selection, Transaction } from "@tiptap/pm/state"
import { CellSelection, selectedRect, TableMap } from "@tiptap/pm/tables"

const tableRowLimit = 50
const tableColumnLimit = 25

export function getNodeFromSchema(schema: Schema, name: string) {
  const node = schema.nodes[name]
  if (!node) {
    // This will only happen if RichTextField is not properly configured with
    // the necessary extensions (table, tableRow, tableHeader and tableCell),
    // so it should not be handling tables.
    throw new Error(`Unwanted state: ${name} not found in schema.`)
  }
  return node
}

// This function was extracted from https://templates.tiptap.dev.
// Permalink: https://github.com/ueberdosis/tiptap-templates/blob/b1147f20c4c28dc071a59f57d58b889b511db168/templates/next-block-editor-app/src/extensions/Table/utils.ts#L23
export function findTable(selection: Selection) {
  return findParentNode((node) => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return (
      node.type.spec["tableRole"] && node.type.spec["tableRole"] === "table"
    )
  })(selection)
}

export function findRow(selection: Selection) {
  return findParentNode((node) => node.type.name === "tableRow")(selection)
}

export function findCell(selection: Selection) {
  return findParentNode((node) => {
    return node.type.name === "tableCell" || node.type.name === "tableHeader"
  })(selection)
}

type FindableNodeName = "cell" | "row" | "table"
const nodeFinders: Record<
  FindableNodeName,
  (selection: Selection) => ReturnType<typeof findParentNodeClosestToPos>
> = {
  cell: findCell,
  row: findRow,
  table: findTable,
}

export function findParentNodes<TNodeName extends FindableNodeName>(
  selection: Selection,
  ...elements: TNodeName[]
) {
  const nodes = {} as {
    [key in TNodeName]: ReturnType<typeof findParentNodeClosestToPos>
  }
  for (let i = 0; i < elements.length; i++) {
    const element = elements[i]
    if (!element) continue
    nodes[element] = nodeFinders[element](selection)
  }
  return nodes
}

export function getTableColumnCount(editor: Editor) {
  const table = findTable(editor.state.selection)?.node
  return table && table.firstChild && table.type.name === "table"
    ? table.firstChild.childCount
    : 0
}

export function getTableRowCount(editor: Editor) {
  const table = findTable(editor.state.selection)?.node
  return table && table.type.name === "table" ? table.childCount : 0
}

export function isTableColumnLimitReached(editor: Editor) {
  return getTableColumnCount(editor) >= tableColumnLimit
}

export function isTableRowLimitReached(editor: Editor) {
  return getTableRowCount(editor) >= tableRowLimit
}

// This function was extracted from https://templates.tiptap.dev and optimized
// for large tables.
// Permalink: https://github.com/ueberdosis/tiptap-templates/blob/b1147f20c4c28dc071a59f57d58b889b511db168/templates/next-block-editor-app/src/extensions/Table/utils.ts#L188.
export function select(
  type: "row" | "column",
  args: { index: number; tr: Transaction },
) {
  const { index, tr } = args

  const tablePos = findTable(tr.selection)
  if (!tablePos) return tr

  const table = TableMap.get(tablePos.node)
  const isRowSelection = type === "row"

  // Check if the index is valid
  if (index < 0 || index >= (isRowSelection ? table.height : table.width)) {
    return tr
  }

  // Define selection bounds: left/right columns and top/bottom rows for
  // selecting either a row or column in the table
  const left = isRowSelection ? 0 : index
  const top = isRowSelection ? index : 0
  const right = isRowSelection ? table.width : index + 1
  const bottom = isRowSelection ? index + 1 : table.height

  // Get the first and last cell positions
  const firstCellPos = table.map[top * table.width + left]
  const lastCellPos = table.map[(bottom - 1) * table.width + (right - 1)]

  if (!firstCellPos || !lastCellPos) return tr

  const head = tablePos.start + firstCellPos
  const anchor = tablePos.start + lastCellPos
  const $head = tr.doc.resolve(head)
  const $anchor = tr.doc.resolve(anchor)

  return tr.setSelection(new CellSelection($anchor, $head))
}

export function selectRow(args: { index: number; tr: Transaction }) {
  return select("row", args)
}

export function selectColumn(args: { index: number; tr: Transaction }) {
  return select("column", args)
}

export function moveRow(args: {
  direction: "up" | "down"
  state: EditorState
}): { tr: Transaction; index: number } | undefined {
  const { direction, state } = args
  const {
    map: tableMap,
    table,
    tableStart,
    top: currentRowIndex,
  } = selectedRect(state)

  // Check boundaries
  if (
    (direction === "up" && currentRowIndex === 0) ||
    (direction === "down" && currentRowIndex >= tableMap.height - 1)
  ) {
    return undefined
  }

  const { tr } = state
  const targetRowIndex =
    direction === "up" ? currentRowIndex - 1 : currentRowIndex + 1

  // Calculate positions for both rows
  let selectedRowPos = tableStart
  for (let i = 0; i < currentRowIndex; i++) {
    selectedRowPos += table.child(i).nodeSize
  }
  let targetRowPos = tableStart
  for (let i = 0; i < targetRowIndex; i++) {
    targetRowPos += table.child(i).nodeSize
  }

  // Get the nodes for both rows
  const selectedRow = table.child(currentRowIndex)
  const targetRow = table.child(targetRowIndex)

  // Swap the rows
  switch (direction) {
    case "up":
      tr.replaceWith(
        selectedRowPos,
        selectedRowPos + selectedRow.nodeSize,
        targetRow,
      )
      tr.replaceWith(
        targetRowPos,
        targetRowPos + targetRow.nodeSize,
        selectedRow,
      )
      break
    case "down":
      tr.replaceWith(
        targetRowPos,
        targetRowPos + targetRow.nodeSize,
        selectedRow,
      )
      tr.replaceWith(
        selectedRowPos,
        selectedRowPos + selectedRow.nodeSize,
        targetRow,
      )
      break
  }

  return { tr, index: targetRowIndex }
}

export function moveColumn(args: {
  direction: "left" | "right"
  state: EditorState
}): { tr: Transaction; index: number } | undefined {
  const { direction, state } = args
  const {
    map: tableMap,
    table,
    left: selectedColumnIndex,
  } = selectedRect(state)

  // Check boundaries
  if (
    (direction === "left" && selectedColumnIndex === 0) ||
    (direction === "right" && selectedColumnIndex >= tableMap.width - 1)
  ) {
    return undefined
  }

  const { tr } = state
  const targetColumnIndex =
    direction === "left" ? selectedColumnIndex - 1 : selectedColumnIndex + 1

  // Go through each row and swap the cells of the two columns
  for (let i = 0; i < table.children.length; i++) {
    const row = table.children[i]
    if (!row) return undefined

    const selectedCell = row.child(selectedColumnIndex)
    const selectedCellPos =
      tableMap.map[i * tableMap.width + selectedColumnIndex]
    if (!selectedCellPos) return undefined // we have to succeed for every cell

    const targetCell = row.child(targetColumnIndex)
    const targetCellPos = tableMap.map[i * tableMap.width + targetColumnIndex]
    if (!targetCellPos) return undefined // we have to succeed for every cell

    switch (direction) {
      case "left": {
        tr.replaceWith(
          selectedCellPos,
          selectedCellPos + selectedCell.nodeSize,
          targetCell,
        )
        tr.replaceWith(
          targetCellPos,
          targetCellPos + targetCell.nodeSize,
          selectedCell,
        )
        break
      }
      case "right": {
        tr.replaceWith(
          targetCellPos,
          targetCellPos + targetCell.nodeSize,
          selectedCell,
        )
        tr.replaceWith(
          selectedCellPos,
          selectedCellPos + selectedCell.nodeSize,
          targetCell,
        )
        break
      }
    }
  }

  return { tr, index: targetColumnIndex }
}
